import { call, put, takeEvery } from "redux-saga/effects";
import { ENDPOINT, PLOT_VIEWER } from "../constants";
import { getDataFromKpiDash } from "../api";
import {
  setPlotImageDataByFileName,
  setPlotViewerError,
  setPlotViewerImageData,
  setPlotViewerLoading,
} from "../actions";

const S3_BUCKET_BASE_URL = process.env.REACT_APP_S3_BUCKET_BASE_URL || "";

const safeParseRows = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  const body = response?.body ?? response?.data ?? response?.rows ?? response;
  if (Array.isArray(body)) {
    return body;
  }

  if (typeof body === "string") {
    try {
      const parsed = JSON.parse(body);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_error) {
      return [];
    }
  }

  return [];
};

const escapeSql = (value) => String(value ?? "").replace(/'/g, "''");

const buildSignedUrl = (objectKey) => {
  if (!objectKey) {
    return "";
  }
  return `${S3_BUCKET_BASE_URL}/${encodeURIComponent(objectKey)}`;
};

const normalizePlotViewerRow = (row, photoCount) => {
  const plotPhotoId = row?.plot_photo_id ?? row?.id ?? "";
  const photographerName = row?.photographer_name ?? row?.name ?? "";
  const photographedDatetimeJst = row?.photographed_datetime_jst ?? "";
  const municipalityName = row?.municipality_name ?? "";
  const assetType = row?.asset_type ?? "";
  const assetSubType = row?.asset_sub_type ?? "";
  const confidenceScore = row?.confidence_score ?? "";
  const plotId = row?.plot_id ?? "";

  return {
    ...row,
    id: plotPhotoId,
    plot_photo_id: plotPhotoId,
    plot_photography_id: row?.plot_photography_id ?? "",
    plot_id: plotId,
    plotId,
    plot_game_id: row?.plot_game_id ?? "",
    plot_game_name: row?.plot_game_name ?? "",
    asset_type: assetType,
    assetType,
    asset_sub_type: assetSubType,
    assetSubType,
    confidence_score: confidenceScore,
    confidenceScore,
    municipality_name: municipalityName,
    municipalityName,
    photographer_name: photographerName,
    photographerName,
    name: photographerName,
    photographed_datetime_jst: photographedDatetimeJst,
    photographedDatetimeJst,
    occurred_datetime_jst: photographedDatetimeJst,
    occurredDatetimeJst: photographedDatetimeJst,
    latitude: row?.latitude ?? "",
    longitude: row?.longitude ?? "",
    photo_count: photoCount,
    photoCount,
    numOfImage: photoCount,
    signedUrl: buildSignedUrl(row?.object_key),
  };
};

export function* handlePlotImageDataByFileName(action) {
  yield put(setPlotViewerLoading(true));
  yield put(setPlotViewerError(""));
  try {
    const fileName = String(action?.payload ?? "");
    const safeFileName = escapeSql(fileName);
    const sqlQuery = {
      query: `
        SELECT
          pp.id AS plot_photo_id,
          pp.plot_photography_id,
          pp.object_key,
          pp.file_name,
          pp.inspection_item_id,
          pp.inspection_option_action_id,
          pp.inspection_photography_angle_of_view_id,
          pph.plot_game_id,
          pph.area_id,
          pph.plot_id,
          pph.player_id,
          CONVERT_TZ(pph.photographed_datetime, '+00:00', '+09:00') AS photographed_datetime_jst,
          pph.longitude,
          pph.latitude,
          p.asset_type,
          p.asset_sub_type,
          p.confidence_score,
          pgi.plot_game_name,
          gfad.name AS municipality_name,
          ps.name AS photographer_name,
          ii.id AS inspection_item_master_id,
          ion.option_name AS inspection_option_name,
          ioa.inspection_action_type,
          ipai.angle_of_view_name AS inspection_photography_angle_of_view_name
        FROM plot_photo pp
        JOIN plot_photography pph
          ON pph.id = pp.plot_photography_id
        LEFT JOIN plot p
          ON p.id = pph.plot_id
        LEFT JOIN plot_game_info pgi
          ON pgi.plot_game_id = pph.plot_game_id
         AND pgi.language_code = 'JA'
        LEFT JOIN game_field_area_display_name gfad
          ON gfad.area_id = pph.area_id
         AND gfad.language_code = 'JA'
        LEFT JOIN player_state ps
          ON ps.player_id = pph.player_id
        LEFT JOIN inspection_item ii
          ON ii.id = pp.inspection_item_id
        LEFT JOIN inspection_option_action ioa
          ON ioa.id = pp.inspection_option_action_id
        LEFT JOIN inspection_option io
          ON io.id = ioa.inspection_option_id
        LEFT JOIN inspection_option_name ion
          ON ion.inspection_option_id = io.id
         AND ion.language_code = 'JA'
        LEFT JOIN inspection_photography_angle_of_view_info ipai
          ON ipai.inspection_photography_angle_of_view_id = pp.inspection_photography_angle_of_view_id
         AND ipai.language_code = 'JA'
        WHERE pp.plot_photography_id = (
          SELECT pp_anchor.plot_photography_id
          FROM plot_photo pp_anchor
          WHERE pp_anchor.file_name = '${safeFileName}'
          ORDER BY pp_anchor.id DESC
          LIMIT 1
        )
        ORDER BY pp.id DESC
      `,
      type: "select",
    };

    const response = yield call(getDataFromKpiDash, sqlQuery, ENDPOINT.GET_DATA_API_REPLICA);
    const rows = safeParseRows(response);
    const normalizedRows = rows.map((row) => normalizePlotViewerRow(row, rows.length));

    yield put(setPlotImageDataByFileName(normalizedRows));
    yield put(setPlotViewerImageData(normalizedRows));

    if (normalizedRows.length === 0) {
      yield put(setPlotViewerError("画像データが見つかりませんでした。"));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("[plotViewerSaga] failed to load plot image data", error);
    yield put(setPlotImageDataByFileName([]));
    yield put(setPlotViewerImageData([]));
    yield put(setPlotViewerError("Plot の画像データ取得に失敗しました。"));
  } finally {
    yield put(setPlotViewerLoading(false));
  }
}

export default function* plotViewerSaga() {
  yield takeEvery(PLOT_VIEWER.GET_PLOT_IMAGE_DATA_BY_FILENAME, handlePlotImageDataByFileName);
}
